import { Component, effect, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';

import {
  VendorRolesService,
  VendorRoleWithStatuses,
  VendorStatusItem,
  UserWithVendorRole,
} from 'src/app/services/vendor-roles.service';
import { StatusTooltipComponent } from '../status-tooltip/status-tooltip.component';

@Component({
  standalone: true,
  selector: 'app-assign-users',
  templateUrl: './assign-users.component.html',
  styleUrls: ['./assign-users.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatTableModule,
    TranslateModule,
    StatusTooltipComponent,
  ],
})
export class AssignUsersComponent {
  orgId = input<number | null>(null);

  users: UserWithVendorRole[] = [];
  assignOrgRoles: VendorRoleWithStatuses[] = [];
  allStatuses: VendorStatusItem[] = [];
  usersLoading = false;
  savingUser: Record<number, boolean> = {};
  displayedColumns: string[] = ['name', 'email', 'vendorRole', 'statuses', 'actions'];

  constructor(private vendorRolesService: VendorRolesService) {
    effect(() => {
      const id = this.orgId();
      this.loadData(id);
    });
  }

  /** Public method so the parent can trigger a refresh (e.g. after roles change). */
  reload(): void {
    this.loadData(this.orgId());
  }

  private loadData(orgId: number | null): void {
    if (!orgId) {
      this.users = [];
      this.assignOrgRoles = [];
      return;
    }

    this.usersLoading = true;

    this.vendorRolesService.getUsersByOrg(orgId).subscribe({
      next: (data) => {
        this.users = data;
        this.usersLoading = false;
      },
      error: () => {
        this.usersLoading = false;
      },
    });

    this.vendorRolesService.getVendorRolesWithStatuses(orgId).subscribe({
      next: (res) => {
        this.assignOrgRoles = res.data;
        this.allStatuses = res.allStatuses;
      },
    });
  }

  onUserRoleChange(user: UserWithVendorRole, newRoleId: number | null): void {
    this.savingUser[user.userId] = true;

    if (newRoleId === null && user.assignmentId) {
      this.vendorRolesService.removeVendorRole(user.assignmentId).subscribe({
        next: () => {
          user.vendorRole = null;
          user.assignmentId = null;
          this.savingUser[user.userId] = false;
        },
        error: () => {
          this.savingUser[user.userId] = false;
        },
      });
    } else if (newRoleId !== null && user.assignmentId) {
      this.vendorRolesService.updateVendorRoleAssignment(user.assignmentId, newRoleId).subscribe({
        next: () => {
          const role = this.assignOrgRoles.find((r) => r.id === newRoleId);
          user.vendorRole = role ? { id: role.id, role_name: role.role_name } : null;
          this.savingUser[user.userId] = false;
        },
        error: () => {
          this.savingUser[user.userId] = false;
        },
      });
    } else if (newRoleId !== null && !user.assignmentId) {
      this.vendorRolesService.assignVendorRole(user.userId, newRoleId).subscribe({
        next: (res) => {
          const role = this.assignOrgRoles.find((r) => r.id === newRoleId);
          user.vendorRole = role ? { id: role.id, role_name: role.role_name } : null;
          user.assignmentId = res.id;
          this.savingUser[user.userId] = false;
        },
        error: () => {
          this.savingUser[user.userId] = false;
        },
      });
    }
  }

  removeUserRole(user: UserWithVendorRole): void {
    if (!user.assignmentId) return;
    this.onUserRoleChange(user, null);
  }

  getVisibleStatusIdsForUser(user: UserWithVendorRole): Set<number> {
    if (!user.vendorRole) return new Set();
    const role = this.assignOrgRoles.find((r) => r.id === user.vendorRole!.id);
    if (!role) return new Set();
    return new Set(role.statuses.map((s) => s.id));
  }

  getUserRoleId(user: UserWithVendorRole): number | null {
    return user.vendorRole?.id ?? null;
  }
}
